import type { LandingAction, LandingLocale } from './landingContentTypes.js';
import { industryApplicationHref } from './industryPageContent.js';
import { resolveApplicationPortalHref } from './landingContentShared.js';
import { isIndustryPageKey } from './industryPages.js';
import type { StaticPageAudience, StaticPageContent, StaticPageKey } from './staticPageContent.js';

const applicationLabels = new Set([
  'Hier anmelden', 'Jetzt bewerben', 'Bewerben', 'Anmelden',
  'Apply now', 'Apply', 'Register here', 'Hemen başvur', 'Başvur', 'Kayıt ol',
]);
const contactLabels: Record<LandingLocale, string> = {
  de: 'Fachkräfte anfragen',
  en: 'Request specialists',
  tr: 'İletişime geç',
};

const companyContactAction = (action: LandingAction | undefined, locale: LandingLocale): LandingAction => ({
  label: action?.label && !applicationLabels.has(action.label) ? action.label : contactLabels[locale],
  href: '#kontakt',
});

const applicationAction = (action: LandingAction, locale: LandingLocale): LandingAction => ({
  ...action, href: resolveApplicationPortalHref(action.href, undefined, locale),
});

// Resolve saved application links for the active environment; company actions open the contact dialog.
export const industryPageForAudience = (
  page: StaticPageContent,
  audience: StaticPageAudience,
  locale: LandingLocale,
): StaticPageContent => {
  if (!isIndustryPageKey(page.key)) return page;
  if (audience !== 'company') return {
    ...page,
    heroAction: page.heroAction ? applicationAction(page.heroAction, locale) : page.heroAction,
    cta: page.cta ? { ...page.cta, primaryAction: applicationAction(page.cta.primaryAction, locale) } : page.cta,
  };
  return {
    ...page,
    heroAction: companyContactAction(page.heroAction, locale),
    cta: page.cta ? { ...page.cta, primaryAction: companyContactAction(page.cta.primaryAction, locale) } : page.cta,
  };
};

export const applyBranchPortalLinks = (
  page: StaticPageContent,
  pageKey: StaticPageKey,
  locale: LandingLocale,
  audience: StaticPageAudience,
): void => {
  if (audience === 'company') {
    Object.assign(page, industryPageForAudience(page, audience, locale));
    return;
  }
  Object.assign(page, industryPageForAudience(page, audience, locale));
  // Preserve the existing enforced form targets for the four original applicant industries.
  if (!['pflege', 'gastro', 'handwerk', 'einzelhandel'].includes(pageKey) || !isIndustryPageKey(pageKey)) return;
  const href = industryApplicationHref(pageKey, locale);
  page.heroAction = { label: page.heroAction?.label ?? 'Hier anmelden', href };
  if (page.cta) page.cta.primaryAction = { label: page.cta.primaryAction?.label ?? 'Hier anmelden', href };
};
